import { API_BASE_URL } from '../../config/api'
import { authFetch } from '../../utils/authFetch'
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Building2,
  Users,
  DollarSign,
  TrendingUp,
  Activity,
  Globe,
  CreditCard,
  AlertCircle,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Database
} from 'lucide-react'

interface PlatformMetrics {
  totalOperators: number
  activeOperators: number
  totalRevenue: number
  monthlyRevenue: number
  totalBookings: number
  revenueGrowth: number
  operatorGrowth: number
  systemHealth: 'healthy' | 'warning' | 'critical'
}

interface RecentActivity {
  id: string
  type: 'operator_signup' | 'payment_processed' | 'system_alert' | 'configuration_change' | 'accommodation_added' | 'activity_added' | 'booking_created'
  description: string
  timestamp: string
  status: 'success' | 'warning' | 'error'
}

const ADMIN_BRAND = {
  primary: '#4F46E5',
  primaryLight: '#EEF2FF',
  primaryText: '#3730A3',
  gradient: 'linear-gradient(to right, #2563EB, #4F46E5)',
}

const SuperAdminOverview: React.FC = () => {
  const navigate = useNavigate()
  const [metrics, setMetrics] = useState<PlatformMetrics>({
    totalOperators: 0,
    activeOperators: 0,
    totalRevenue: 0,
    monthlyRevenue: 0,
    totalBookings: 0,
    revenueGrowth: 0,
    operatorGrowth: 0,
    systemHealth: 'healthy'
  })
  const [loading, setLoading] = useState(true)

  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([])

  // Fetch platform metrics and recent activity from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        
        // Fetch platform metrics
        const metricsResponse = await authFetch(`${API_BASE_URL}/api/super-admin/platform-metrics`)
        if (metricsResponse.ok) {
          const metricsData = await metricsResponse.json()
          if (metricsData.success) {
            const apiMetrics = metricsData.metrics

            const newMetrics = {
              totalOperators: apiMetrics.totalOperators,
              activeOperators: apiMetrics.activeOperators,
              totalRevenue: 0, // Revenue calculation to be implemented
              monthlyRevenue: 0, // Monthly revenue calculation to be implemented
              totalBookings: apiMetrics.totalBookings,
              revenueGrowth: 0, // Growth calculation to be implemented
              operatorGrowth: apiMetrics.totalOperators > 0 ?
                ((apiMetrics.activeOperators / apiMetrics.totalOperators) * 100) : 0,
              systemHealth: apiMetrics.systemHealth
            }
            setMetrics(newMetrics)
          } else {
            console.error('Platform metrics API returned error:', metricsData)
          }
        } else {
          console.error('Platform metrics request failed with status:', metricsResponse.status)
        }

        // Fetch recent activity
        const activityResponse = await authFetch(`${API_BASE_URL}/api/super-admin/recent-activity`)
        if (activityResponse.ok) {
          const activityData = await activityResponse.json()
          if (activityData.success) {
            const formattedActivity = activityData.activities.map((activity: any) => ({
              id: activity.id,
              type: activity.type,
              description: activity.description,
              timestamp: new Date(activity.timestamp).toLocaleString(),
              status: activity.status
            }))
            setRecentActivity(formattedActivity)
          }
        }

        // Fetch additional analytics for better overview metrics
        const analyticsResponse = await authFetch(`${API_BASE_URL}/api/super-admin/analytics?time_range=30d`)
        if (analyticsResponse.ok) {
          const analyticsData = await analyticsResponse.json()
          if (analyticsData.success) {
            const analytics = analyticsData.analytics
            // Update metrics with real analytics data
            setMetrics(prevMetrics => ({
              ...prevMetrics,
              totalRevenue: analytics.revenue.total,
              monthlyRevenue: analytics.revenue.monthly,
              revenueGrowth: analytics.revenue.growth,
              totalBookings: analytics.bookings.total
            }))
          }
        }
      } catch (error) {
        console.error('Error fetching super admin data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const stats = [
    {
      name: 'Total B2B Operators',
      value: metrics.totalOperators,
      change: metrics.operatorGrowth,
      changeType: metrics.operatorGrowth >= 0 ? 'positive' as const : 'negative' as const,
      icon: Building2,
      color: 'blue'
    },
    {
      name: 'Active This Month',
      value: metrics.activeOperators,
      change: 0,
      changeType: 'positive' as const,
      icon: Users,
      color: 'green'
    },
    {
      name: 'Monthly Revenue',
      value: `$${metrics.monthlyRevenue.toLocaleString()}`,
      change: metrics.revenueGrowth,
      changeType: metrics.revenueGrowth >= 0 ? 'positive' as const : 'negative' as const,
      icon: DollarSign,
      color: 'emerald'
    },
    {
      name: 'Total Platform Revenue',
      value: `$${metrics.totalRevenue.toLocaleString()}`,
      change: 0,
      changeType: 'positive' as const,
      icon: TrendingUp,
      color: '',
      brandStyle: { bg: ADMIN_BRAND.primaryLight, text: ADMIN_BRAND.primary }
    }
  ]

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'operator_signup': return Users
      case 'payment_processed': return CreditCard
      case 'system_alert': return AlertCircle
      case 'configuration_change': return Database
      case 'accommodation_added': return Building2
      case 'activity_added': return Activity
      case 'booking_created': return CreditCard
      default: return Activity
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-600 bg-green-100'
      case 'warning': return 'text-yellow-600 bg-yellow-100'
      case 'error': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl p-6 text-white" style={{ background: ADMIN_BRAND.gradient }}>
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mr-3"></div>
            <span className="text-lg">Loading platform metrics...</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-xl p-6 text-white" style={{ background: ADMIN_BRAND.gradient }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Platform Overview</h1>
            <p className="text-blue-100 mt-1">AI Booking Engine SaaS Platform Dashboard</p>
          </div>
          <Globe className="h-12 w-12 text-blue-200" />
        </div>
        
        <div className="mt-6">
          <div className="bg-white/10 rounded-lg p-3 inline-flex items-center">
            <CheckCircle2 className="h-5 w-5 text-green-300 mr-2" />
            <span className="text-sm">{metrics.totalOperators} operators, {metrics.totalBookings} bookings tracked</span>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon
          return (
            <div key={stat.name} className="bg-white overflow-hidden rounded-xl shadow-sm border border-gray-100">
              <div className="p-5">
                <div className="flex items-center">
                  <div className={`flex-shrink-0 rounded-lg p-3 ${stat.brandStyle ? '' : `bg-${stat.color}-100`}`} style={stat.brandStyle ? { backgroundColor: stat.brandStyle.bg } : undefined}>
                    <Icon className={`h-6 w-6 ${stat.brandStyle ? '' : `text-${stat.color}-600`}`} style={stat.brandStyle ? { color: stat.brandStyle.text } : undefined} />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{stat.name}</dt>
                      <dd className="text-lg font-semibold text-gray-900">{stat.value}</dd>
                    </dl>
                  </div>
                </div>
                <div className="mt-4 flex items-center">
                  <div className={`flex items-center text-sm ${stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'}`}>
                    {stat.changeType === 'positive' ? (
                      <ArrowUpRight className="h-4 w-4 mr-1" />
                    ) : (
                      <ArrowDownRight className="h-4 w-4 mr-1" />
                    )}
                    {Math.abs(stat.change).toFixed(1)}%
                  </div>
                  <span className="ml-2 text-sm text-gray-500">from last month</span>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Activity className="h-5 w-5 text-gray-400 mr-2" />
              Recent Platform Activity
            </h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {recentActivity.map((activity) => {
                const Icon = getActivityIcon(activity.type)
                return (
                  <div key={activity.id} className="flex items-start space-x-3">
                    <div className={`flex-shrink-0 rounded-full p-2 ${getStatusColor(activity.status)}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{activity.description}</p>
                      <p className="text-xs text-gray-500 flex items-center mt-1">
                        <Clock className="h-3 w-3 mr-1" />
                        {activity.timestamp}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <button onClick={() => navigate('/super-admin/operators')} className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                <Users className="h-4 w-4 mr-2" />
                Add Operator
              </button>
              <button onClick={() => navigate('/super-admin/subscriptions')} className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                <CreditCard className="h-4 w-4 mr-2" />
                View Billing
              </button>
              <button onClick={() => navigate('/super-admin/configuration')} className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                <Database className="h-4 w-4 mr-2" />
                System Logs
              </button>
              <button onClick={() => navigate('/super-admin/analytics')} className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                <TrendingUp className="h-4 w-4 mr-2" />
                Analytics
              </button>
            </div>

            {/* System Health */}
            <div className="mt-6 p-4 bg-green-50 rounded-lg">
              <div className="flex items-center">
                <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
                <div>
                  <h4 className="text-sm font-medium text-green-800">All Systems Operational</h4>
                  <p className="text-xs text-green-600">Platform running normally</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Platform Summary */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Platform Summary</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{metrics.totalBookings.toLocaleString()}</div>
              <div className="text-sm text-gray-500">Total Bookings Processed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{metrics.totalOperators}</div>
              <div className="text-sm text-gray-500">Registered Operators</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: ADMIN_BRAND.primary }}>{metrics.activeOperators}</div>
              <div className="text-sm text-gray-500">Active This Month</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SuperAdminOverview